import type { RecommendationInput } from "@shared/recommendation";

/** DB shape for team_members.gift_preferences (jsonb). */
export interface GiftPreferencesObject {
  vibes?: string[];
  avoid?: string[];
  desired_categories?: string[];
  notes?: string;
}

/** Minimal team_members row slice needed for recommendation hints. */
export interface TeamMemberProfileSource {
  hobbies?: string[] | null;
  favourite_foods?: string[] | null;
  gift_preferences?: unknown;
  personal_notes?: string | null;
  // Accept arbitrary team-member shapes (celebration rows carry no hint
  // fields); without this index signature the all-optional interface is a
  // "weak type" and TS rejects objects that share none of its keys.
  [key: string]: unknown;
}

export function normalizeGiftPreferences(
  raw: TeamMemberProfileSource["gift_preferences"],
): GiftPreferencesObject | undefined {
  if (!raw) return undefined;
  if (Array.isArray(raw)) {
    return raw.length > 0 ? { vibes: raw } : undefined;
  }
  const gp = raw as GiftPreferencesObject;
  const hasContent =
    (gp.vibes ?? []).length > 0 ||
    (gp.avoid ?? []).length > 0 ||
    (gp.desired_categories ?? []).length > 0 ||
    (gp.notes?.trim().length ?? 0) > 0;
  return hasContent ? gp : undefined;
}

/** Extract profile hints for the recommendation engine from a team_members row. */
export function teamMemberProfileHints(
  member: TeamMemberProfileSource | null | undefined,
): Pick<
  RecommendationInput,
  "hobbies" | "favourite_foods" | "gift_preferences" | "personal_notes"
> {
  if (!member) return {};
  const gift_preferences = normalizeGiftPreferences(member.gift_preferences);
  return {
    hobbies: member.hobbies?.length ? member.hobbies : undefined,
    favourite_foods: member.favourite_foods?.length ? member.favourite_foods : undefined,
    gift_preferences,
    personal_notes: member.personal_notes?.trim() ? member.personal_notes : undefined,
  };
}
